import { useEffect, useState, useMemo } from 'react'
import { SecondaryBase } from './../Commons/SecondaryBase'
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'
import { Prepare as BLPrepare, Start as BLStart, Stop as BLStop, GetAllPresets, CreatePreset, UpdatePreset, DeletePreset } from './../../../utilities/BL/Admin/Games/LightShow'
import { Confirm } from './../../Commons/Modals/Confirm'
import { ValidateSqlInjection } from './../../../utilities/BL/Security' // <--- IMPORTACIÓN DE SEGURIDAD

const LightShow = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage()

    const [beatsPerMinute, setBeatsPerMinute] = useState(118);
    const [currentSystemStatus, setCurrentSystemStatus] = useState('Juego inactivo. Configura el ritmo y colores.');
    const [animationFrames, setAnimationFrames] = useState([{ id: 1, color: '#047633', flashOn: true }, { id: 2, color: '#000000', flashOn: false }]);

    // ==========================================
    // ESTADOS DE PRESETS (Base de Datos)
    // ==========================================
    const [presetsDb, setPresetsDb] = useState([]);
    const [activePreset, setActivePreset] = useState(null);
    const [isLoadingPresets, setIsLoadingPresets] = useState(true);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    // ==========================================
    // CARGA INICIAL DESDE BD
    // ==========================================
    const loadPresetsFromDb = async () => {
        setIsLoadingPresets(true);
        const res = await GetAllPresets();
        if (res.success) {
            setPresetsDb(res.data);
            // Autocargar el primer preset si no hay nada activo
            if (!activePreset && res.data.length > 0) {
                applyPresetToUI(res.data[0]);
            }
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
        setIsLoadingPresets(false);
    };

    useEffect(() => {
        loadPresetsFromDb();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ==========================================
    // INDICADOR DE "CAMBIOS SIN GUARDAR"
    // ==========================================
    const hasUnsavedChanges = useMemo(() => {
        if (!activePreset) return true;
        if (activePreset.beatsPerMinute !== beatsPerMinute) return true;
        
        try {
            const currentSimple = animationFrames.map(f => ({ c: f.color.toLowerCase(), f: f.flashOn }));
            const savedDbFrames = JSON.parse(activePreset.framesJson);
            const savedSimple = savedDbFrames.map(f => ({ c: (f.color||'').toLowerCase(), f: f.flashOn }));
            return JSON.stringify(currentSimple) !== JSON.stringify(savedSimple);
        } catch (e) {
            return true;
        }
    }, [activePreset, beatsPerMinute, animationFrames]);

    // ==========================================
    // MANEJO DE PRESETS EN UI
    // ==========================================
    const applyPresetToUI = (preset) => {
        try {
            const parsedFrames = JSON.parse(preset.framesJson);
            setAnimationFrames(parsedFrames);
            setBeatsPerMinute(preset.beatsPerMinute);
            setActivePreset(preset);
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al leer los colores de este preset.' });
        }
    };

    // DEBE retornar { success: true } para que el Modal Confirm se cierre.
    const handleSaveUpdate = async () => {
        if (!activePreset) return { success: false, message: 'No hay preset activo.' };
        
        const res = await UpdatePreset(activePreset.id, activePreset.name, beatsPerMinute, animationFrames);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            // Actualizamos la referencia local para que el asterisco de "Sin Guardar" desaparezca
            setActivePreset(prev => ({ ...prev, beatsPerMinute, framesJson: JSON.stringify(animationFrames) }));
            await loadPresetsFromDb(); 
            return { success: true }; // <--- Cierra el modal
        } else {
            return { success: false, message: res.message }; // <--- Muestra error en el modal
        }
    };

    // No usa Modal Confirm, porque el window.prompt bloquea nativamente
    const handleSaveAsNew = async () => {
        const newName = window.prompt("Ingresa un nombre para tu preset personalizado:");
        if (!newName || newName.trim() === '') return;

        // Validar inyección SQL
        const isMalicious = await ValidateSqlInjection(newName);
        if (isMalicious) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre ingresado contiene caracteres o palabras no permitidas por seguridad.' });
            return;
        }

        const res = await CreatePreset(newName, beatsPerMinute, animationFrames);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            await loadPresetsFromDb();
            applyPresetToUI(res.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
    };

    // DEBE retornar { success: true } para que el Modal Confirm se cierre.
    const handleDeletePreset = async () => {
        if (!activePreset) return { success: false, message: 'No hay preset seleccionado.' };
        if (activePreset.isSystemPreset) return { success: false, message: 'No se puede eliminar un preset del sistema.' };

        const res = await DeletePreset(activePreset.id);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            setActivePreset(null);
            await loadPresetsFromDb();
            return { success: true };
        } else {
            return { success: false, message: res.message };
        }
    };

    // AGREGAMOS el async y el return { success: true } para que cierre el modal
    const handleRestoreDefault = async () => {
        if (activePreset) {
            applyPresetToUI(activePreset);
        }
        return { success: true };
    };

    // ==========================================
    // MÉTODOS DEL CONSTRUCTOR DE FRAMES
    // ==========================================
    const addFrame = () => {
        // Lee el último frame para alternar el prendido/apagado
        const lastFrame = animationFrames.length > 0 ? animationFrames[animationFrames.length - 1] : null;
        const nextFlashState = lastFrame ? !lastFrame.flashOn : true;

        const newFrame = { id: Date.now(), color: '#047633', flashOn: nextFlashState };
        setAnimationFrames([...animationFrames, newFrame]);
    };

    const removeFrame = (frameIdToRemove) => {
        if (animationFrames.length <= 2) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes tener al menos 2 frames.' });
            return;
        }
        setAnimationFrames(animationFrames.filter(frame => frame.id !== frameIdToRemove));
    };

    const updateFrame = (frameIdToUpdate, propertyName, newValue) => {
        setAnimationFrames(prev => prev.map(frame => frame.id === frameIdToUpdate ? { ...frame, [propertyName]: newValue } : frame));
    };

    const moveFrameUp = (index) => {
        if (index === 0) return;
        const newFrames = [...animationFrames];
        [newFrames[index - 1], newFrames[index]] = [newFrames[index], newFrames[index - 1]];
        setAnimationFrames(newFrames);
    };

    const moveFrameDown = (index) => {
        if (index === animationFrames.length - 1) return;
        const newFrames = [...animationFrames];
        [newFrames[index + 1], newFrames[index]] = [newFrames[index], newFrames[index + 1]];
        setAnimationFrames(newFrames);
    };

    // ==========================================
    // MÉTODOS DE LA BATUTA (Show en vivo)
    // ==========================================
    
    // DEBE retornar { success: true } para que el Modal Confirm se cierre.
    const executeQuickStartShow = async () => {
        setCurrentSystemStatus('Iniciando proceso...');
        
        const prepResponse = await BLPrepare(beatsPerMinute, animationFrames);
        if (!prepResponse?.success) {
            setCurrentSystemStatus('Error en la preparación.');
            return { success: false, message: prepResponse?.message };
        }

        const startResponse = await BLStart(beatsPerMinute, animationFrames);
        if (!startResponse?.success) {
            setCurrentSystemStatus('Error al iniciar el show.');
            return { success: false, message: startResponse?.message };
        }

        setCurrentSystemStatus(`¡SHOW EN CURSO! 🌟 (${beatsPerMinute} BPM)`);
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'El show de luces está corriendo exitosamente' });
        return { success: true }; // <--- Cierra el modal
    };

    // DEBE retornar { success: true } para que el Modal Confirm se cierre.
    const executeStopShow = async () => {
        const response = await BLStop();

        if (response?.success) {
            setCurrentSystemStatus('Show Detenido 🛑');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Luces apagadas exitosamente.' });
            return { success: true }; // <--- Cierra el modal
        }

        return { success: false, message: response?.message }; // <--- Muestra error en el modal
    };

    const triggerModal = (messageToDisplay, actionFunction, yesButtonText) => {
        setConfirmModalData({ message: messageToDisplay, actionToYesButton: actionFunction, textYesButton: yesButtonText, textNoButton: 'Cancelar' });
        setShowConfirmModal(true);
    };

    const ReturnToDashboard = async () => {
        await BLResetCurrentDynamic();
        getOutOfTheDynamic();
    };

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                <p className='AdmRunToGoal__Title mb-3 mb-md-0'>
                    <i className='bi bi-phone-vibrate me-2'></i> Show de Luces
                </p>
                <div className="alert alert-dark py-2 px-3 mb-0 font-weight-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
                    <i className="bi bi-broadcast-pin me-2"></i> Status: {currentSystemStatus}
                </div>
            </div>

            <div className="row">
                <div className="col-md-7 mb-4">
                    <div className="card shadow-sm h-100 border-dark">
                        
                        {/* ============================================== */}
                        {/* CABECERA REDISEÑADA (Más intuitiva y arriba) */}
                        {/* ============================================== */}
                        <div className="card-header bg-dark text-white p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="font-weight-bold"><i className="bi bi-palette-fill me-2"></i> 1. Ciclo de Colores</span>
                                <div className="d-flex align-items-center">
                                    <label className="mb-0 me-2 small fw-bold">BPM (Ritmo):</label>
                                    <input 
                                        type="number" className="form-control form-control-sm text-center font-weight-bold text-dark" 
                                        style={{ width: '80px' }} value={beatsPerMinute} 
                                        onChange={(e) => setBeatsPerMinute(parseInt(e.target.value) || 0)} 
                                    />
                                </div>
                            </div>
                            
                            {/* PANEL DE PRESET ACTIVO */}
                            <div className="bg-secondary p-2 rounded d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                                <div>
                                    <span className="small text-light">Preset:</span>
                                    <span className={`ms-2 badge ${activePreset?.isSystemPreset ? 'bg-dark text-light' : 'bg-primary'} fs-6`}>
                                        {activePreset ? activePreset.name : 'Personalizado'}
                                        {/* {activePreset?.isSystemPreset && <i className="bi bi-shield-lock-fill ms-2" title="Sistema"></i>} */}
                                    </span>
                                    {hasUnsavedChanges && <span className="ms-2 text-warning font-weight-bold small">* Editado</span>}
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {activePreset && hasUnsavedChanges && (
                                        <button className="btn btn-sm btn-success font-weight-bold shadow-sm" 
                                            // INYECCIÓN DE NOMBRE DINÁMICO
                                            onClick={() => triggerModal(`¿Deseas guardar los cambios y sobrescribir el preset "${activePreset.name}"?`, handleSaveUpdate, 'Guardar cambios')}>
                                            <i className="bi bi-floppy-fill me-1"></i> Guardar cambios
                                        </button>
                                    )}

                                    {hasUnsavedChanges && activePreset && (
                                        <button className="btn btn-sm btn-danger" 
                                            // INYECCIÓN DE NOMBRE DINÁMICO
                                            onClick={() => triggerModal(`¿Deseas descartar los cambios y regresar a los colores originales del preset "${activePreset.name}"?`, handleRestoreDefault, 'Sí, descartar cambios')}>
                                            Descartar cambios
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="card-body p-0">
                            <table className="table table-bordered table-hover mb-0 align-middle text-center">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{width: '60px'}}>Orden</th>
                                        <th>Color de pantalla</th>
                                        <th>¿Flash prendido o apagado?</th>
                                        <th style={{width: '60px'}}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {animationFrames.map((frame, index) => (
                                        <tr key={frame.id}>
                                            <td>
                                                <div className="d-flex flex-column align-items-center">
                                                    <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameUp(index)} disabled={index===0}><i className="bi bi-caret-up-fill"></i></button>
                                                    <span className="font-weight-bold small">{index + 1}</span>
                                                    <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameDown(index)} disabled={index===animationFrames.length-1}><i className="bi bi-caret-down-fill"></i></button>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex justify-content-center align-items-center">
                                                    <input 
                                                        type="color" className="form-control form-control-color border-0 p-0 me-2" 
                                                        value={frame.color} onChange={(e) => updateFrame(frame.id, 'color', e.target.value)} 
                                                        style={{ width: '40px', height: '30px' }} 
                                                    />
                                                    <span className="small text-muted font-monospace">{frame.color.toUpperCase()}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="form-check form-switch d-flex justify-content-center">
                                                    <input 
                                                        className="form-check-input" type="checkbox" checked={frame.flashOn} 
                                                        onChange={(e) => updateFrame(frame.id, 'flashOn', e.target.checked)} 
                                                        style={{ cursor: 'pointer', width: '40px', height: '20px' }} 
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => removeFrame(frame.id)} title="Borrar Frame">
                                                    <i className="bi bi-trash3-fill"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="card-footer bg-light">
                            <button className="btn btn-sm btn-outline-success font-weight-bold mb-3" onClick={addFrame}>
                                <i className="bi bi-plus-circle-fill me-1"></i> Agregar Frame
                            </button>

                            <hr className="my-2"/>

                            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                <span className="text-muted small font-weight-bold">Cambiar a:</span>
                                
                                {/* SELECTOR HÍBRIDO (Botones si <=10, Lista si >10) */}
                                {isLoadingPresets ? (
                                    <span className="small text-muted">Cargando de BD...</span>
                                ) : presetsDb.length <= 10 ? (
                                    presetsDb.map(p => (
                                        <button 
                                            key={p.id} 
                                            className={`btn btn-sm ${activePreset?.id === p.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => applyPresetToUI(p)}
                                        >
                                            {p.name} {/*p.isSystemPreset && <i className="bi bi-lock-fill ms-1 opacity-50" title="Preset del Sistema"></i>*/}
                                        </button>
                                    ))
                                ) : (
                                    <select 
                                        className="form-select form-select-sm w-auto" 
                                        value={activePreset?.id || ''} 
                                        onChange={(e) => applyPresetToUI(presetsDb.find(p => p.id === parseInt(e.target.value)))}
                                    >
                                        <option value="" disabled>-- Selecciona un Preset --</option>
                                        {presetsDb.map(p => (
                                            <option key={p.id} value={p.id}>{p.name} {p.isSystemPreset ? '(Sistema)' : ''}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* BOTONES DE PELIGRO Y RESTAURACIÓN */}
                            <div className="d-flex flex-wrap gap-2 mt-3">
                                <button className="btn btn-sm btn-light font-weight-bold" onClick={handleSaveAsNew}>
                                    <i className="bi bi-plus-circle text-primary me-1"></i> Crear preset personalizado
                                </button>

                                {activePreset && !activePreset.isSystemPreset && (
                                    <button className="btn btn-sm btn-outline-danger ms-auto" 
                                        // INYECCIÓN DE NOMBRE DINÁMICO
                                        onClick={() => triggerModal(`¿Estás seguro de eliminar permanentemente el preset "${activePreset.name}"?`, handleDeletePreset, 'Eliminar preset')}>
                                        <i className="bi bi-trash"></i> Eliminar preset
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-5 mb-4">
                    <div className="card shadow-sm h-100 border-primary">
                        <div className="card-header bg-primary text-white font-weight-bold">
                            <i className="bi bi-joystick me-2"></i> 2. Controles Maestros
                        </div>
                        <div className="card-body d-flex flex-column">
                            <div className="alert alert-primary bg-light border-primary pb-3 pt-3 mb-4 shadow-sm">
                                <h6 className="font-weight-bold text-primary text-center mb-3">Indicaciones</h6>
                                <ol className="mb-0 small text-dark pl-3">
                                    <li className="mb-2"><strong>Iniciar:</strong> Pide permisos automáticamente a los celulares y comienza el parpadeo al ritmo seleccionado en un solo paso.</li>
                                    <li><strong>Detener:</strong> Apaga los flashes y libera las cámaras de los usuarios.</li>
                                </ol>
                            </div>

                            <button className="btn btn-success mb-4 btn-lg font-weight-bold shadow" 
                                onClick={() => triggerModal('¿Deseas iniciar/actualizar el show de luces con los BPM y colores actuales?', executeQuickStartShow, 'Sí, iniciar / actualizar')}>
                                <i className="bi bi-lightning-charge-fill me-1"></i> Iniciar / Actualizar show
                            </button>

                            <button className="btn btn-danger font-weight-bold w-100" 
                                onClick={() => triggerModal('¿Detener el show y apagar las luces?', executeStopShow, 'Sí, detener show')}>
                                <i className="bi bi-stop-circle-fill me-1"></i> Detener / Apagar Luces
                            </button>

                            {/* <hr className="w-100 mt-auto" />

                            <button className="btn btn-danger font-weight-bold w-100" 
                                onClick={() => triggerModal('¿Detener el show y apagar las luces?', executeStopShow, 'Sí, detener show')}>
                                <i className="bi bi-stop-circle-fill me-1"></i> Detener / Apagar Luces
                            </button> */}
                        </div>
                    </div>
                </div>
            </div>

            {showConfirmModal && (
                <Confirm 
                    message={confirmModalData?.message} 
                    actionToYesButton={confirmModalData?.actionToYesButton} 
                    textYesButton={confirmModalData?.textYesButton} 
                    textNoButton={confirmModalData?.textNoButton} 
                    setShowModal={setShowConfirmModal} 
                    setConfirmData={setConfirmModalData} 
                />
            )}
        </SecondaryBase>
    )
}

export { LightShow };









// Funcional
// import { useEffect, useState } from 'react'

// import { SecondaryBase } from './../Commons/SecondaryBase'

// import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
// import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'
// import { Prepare as BLPrepare, Start as BLStart, Stop as BLStop } from './../../../utilities/BL/Admin/Games/LightShow'

// import { Confirm } from './../../Commons/Modals/Confirm'


// /**
//  * Componente LightShow (Admin)
//  * Panel de control para el operador de la dinámica de Sincronización de Flashes.
//  */
// const LightShow = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage()

//     const PRESETS = {
//         KILOMETROS_KARAOKE: 1,
//         NOPODRAS_KARAOKE: 2,
//         BASICO: 3,
//         SIRENA: 4
//     }

//     // ==========================================
//     // 1. ESTADOS DE CONFIGURACIÓN DEL SHOW
//     // ==========================================
//     const [beatsPerMinute, setBeatsPerMinute] = useState(118);
//     const [currentSystemStatus, setCurrentSystemStatus] = useState('Juego inactivo. Configura el ritmo y colores.');

//     // Configuracion por defecto "Clásica" (Sirve para resetear la memoria)
//     const defaultBasicFrames = [
//         //{ id: 1, color: '#FFFFFF', flashOn: true },
//         { id: 1, color: '#d6352d', flashOn: true },
//         { id: 2, color: '#000000', flashOn: false }
//     ];

//     // Estado principal del ciclo de colores/luz
//     const [animationFrames, setAnimationFrames] = useState(defaultBasicFrames);

//     // ==========================================
//     // 2. ESTADOS DEL MODAL DE CONFIRMACIÓN
//     // ==========================================
//     const [showConfirmModal, setShowConfirmModal] = useState(false);
//     const [confirmModalData, setConfirmModalData] = useState(null);

//     const [presetLoaded, setPresetLoaded] = useState(null);

//     // ==========================================
//     // 3. RECUPERACIÓN DE MEMORIA LOCAL (On Mount)
//     // ==========================================
//     useEffect(() => {
//         // Al cargar el componente, intentamos buscar si el Admin ya había guardado un ciclo
//         const savedCustomFrames = localStorage.getItem('lightShowCustomFrames');
//         const savedCustomBPM = localStorage.getItem('lightShowCustomBPM');

//         if (savedCustomFrames) {
//             setAnimationFrames(JSON.parse(savedCustomFrames));
//         }
//         if (savedCustomBPM) {
//             setBeatsPerMinute(parseInt(savedCustomBPM, 10));
//         }
//     }, []);

//     // ==========================================
//     // 4. MÉTODOS DEL CONSTRUCTOR DE FRAMES
//     // ==========================================
//     const addFrame = () => {
//         // Usamos Date.now() para asegurar un ID único para el mapeo de React
//         const newFrame = { id: Date.now(), color: '#FF0000', flashOn: true };
//         setAnimationFrames([...animationFrames, newFrame]);
//     };

//     const removeFrame = (frameIdToRemove) => {
//         if (animationFrames.length <= 2) {
//             showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes tener al menos 2 frames para crear un ciclo válido.' });
//             return;
//         }
//         setAnimationFrames(animationFrames.filter(frame => frame.id !== frameIdToRemove));
//     };

//     const updateFrame = (frameIdToUpdate, propertyName, newValue) => {
//         setAnimationFrames(previousFrames => 
//             previousFrames.map(frame => 
//                 frame.id === frameIdToUpdate ? { ...frame, [propertyName]: newValue } : frame
//             )
//         );
//     };

//     const loadPreset = (presetType) => {
//         try {
//             //if (presetType.toLowerCase() === 'NoPodrasKaraoke'.toLowerCase()) {
//             if (presetType === PRESETS.NOPODRAS_KARAOKE) {
//                 setAnimationFrames([
//                     { id: 1, color: '#d6352d', flashOn: true },  // Azul prende
//                     { id: 2, color: '#000000', flashOn: false } // Apaga
//                 ]);
//                 setBeatsPerMinute(118); // Ajustamos el ritmo ideal para la sirena
//                 setPresetLoaded(PRESETS.NOPODRAS_KARAOKE)
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Preset No podrás (Karaoke) cargado exitosamente' });
    
//             //} else if (presetType.toLowerCase() === 'KilometrosKaraoke'.toLowerCase()) {
//             } else if (presetType === PRESETS.KILOMETROS_KARAOKE) {
//                 setAnimationFrames([
//                     { id: 1, color: '#d6352d', flashOn: true },  // Azul prende
//                     { id: 2, color: '#000000', flashOn: false } // Apaga
//                 ]);
//                 setBeatsPerMinute(188); // Ajustamos el ritmo ideal para la sirena
//                 setPresetLoaded(PRESETS.KILOMETROS_KARAOKE)
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Preset Kilómetros (Karaoke) cargado exitosamente' });
    
//             //} else if (presetType.toLowerCase() === 'basic'.toLowerCase()) {
//             } else if (presetType === PRESETS.BASICO) {
//                 setAnimationFrames(defaultBasicFrames);
//                 setBeatsPerMinute(118);
//                 setPresetLoaded(PRESETS.BASICO)
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Preset Básico cargado exitosamente' });
    
//             //} else if (presetType.toLowerCase() === 'police'.toLowerCase()) {
//             } else if (presetType === PRESETS.SIRENA) {
//                 setAnimationFrames([
//                     { id: 1, color: '#0000FF', flashOn: true },  // Azul prende
//                     { id: 2, color: '#000000', flashOn: false }, // Apaga
//                     { id: 3, color: '#d6352d', flashOn: true },  // Rojo prende
//                     { id: 4, color: '#000000', flashOn: false }  // Apaga
//                 ]);
//                 setBeatsPerMinute(180); // Ajustamos el ritmo ideal para la sirena
//                 setPresetLoaded(PRESETS.SIRENA)
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Preset Sirena cargado exitosamente' });
//             }
//         } catch (error) {
//             setPresetLoaded(null)
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar cargar el preset seleccionado. Inténtalo nuevamente o inténtalo más tarde' });
//         }
//     };

//     // --- NUEVOS MÉTODOS DE MEMORIA LOCAL ---
//     const saveCustomCycleToMemory = () => {
//         localStorage.setItem('lightShowCustomFrames', JSON.stringify(animationFrames));
//         localStorage.setItem('lightShowCustomBPM', beatsPerMinute.toString());
//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tu ciclo de colores y BPM se han guardado correctamente.' });
//     };

//     const clearCustomCycleFromMemory = () => {
//         localStorage.removeItem('lightShowCustomFrames');
//         localStorage.removeItem('lightShowCustomBPM');
        
//         // Restablecemos el panel a la configuración estándar
//         setAnimationFrames(defaultBasicFrames);
//         setBeatsPerMinute(118);
        
//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Memoria borrada con éxito. Se ha restablecido el ciclo por defecto.' });
//     };

//     // ==========================================
//     // 5. MÉTODOS DE LA BATUTA (Conexión API via BL)
//     // ==========================================
//     const executePrepareShow = async () => {
//         try {
//             showMessage({ type: MESSAGE_TYPE.INFO, text: 'Enviando pantalla de permiso a los aficionados...' });
//             const response = await BLPrepare(beatsPerMinute, animationFrames);
    
//             if (response?.success) {
//                 setCurrentSystemStatus('Modo Preparación: Los celulares están pidiendo permisos.');
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Señal de preparación enviada a los aficionados' });
//                 return { success: true };    
//             }

//             showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || 'Ha habido un error al intentar enviar la pantalla de permiso a los aficionados' });
//             return { success: false, message: response?.message };
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar enviar la pantalla de permiso a los aficionados' });
//         }
//     };

//     // const executeStartShow = async () => {
//     //     const response = await BLStart();
//     //     if (response?.success) {
//     //         setCurrentSystemStatus(`¡SHOW EN CURSO! 🌟 (${beatsPerMinute} BPM)`);
//     //         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡El show de luces está corriendo en los dispositivos!' });
//     //         return { success: true };
//     //     }
//     //     return { success: false, message: response?.message };
//     // };

//     const executeStartShow = async () => {
//         try {
//             // AHORA MANDAMOS LA CONFIGURACIÓN TAMBIÉN EN EL START
//             showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciado el encendido de las luces de los aficionados...' });
//             const response = await BLStart(beatsPerMinute, animationFrames);
    
//             if (response?.success) {
//                 setCurrentSystemStatus(`¡SHOW EN CURSO! 🌟 (${beatsPerMinute} BPM)`);
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'El show de luces está corriendo exitosamente en los dispositivos' });
//                 return { success: true };
//             }

//             showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || 'Ha habido un error al intentar encender las luces de los aficionados' });
//             return { success: false, message: response?.message };
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar encender las luces de los aficionados' });
//         }
//     };

//     const executeStopShow = async () => {
//         try {
//             showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciado apagado de luces de los aficionados...' });
//             const response = await BLStop();
    
//             if (response?.success) {
//                 setCurrentSystemStatus('Show Detenido 🛑 (Mantiene permisos en usuarios)');
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Las luces de todos los dispositivos se han apagado exitosamente' });
//                 return { success: true };
//             }

//             showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || 'Ha habido un error al intentar apagar las luces de los aficionados' });
//             return { success: false, message: response?.message };
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar apagar las luces de los aficionados' });
//         }
//     };

//     // Wrapper para abrir el Modal genérico Confirm
//     const triggerModal = (messageToDisplay, actionFunction, yesButtonText) => {
//         setConfirmModalData({ 
//             message: messageToDisplay, 
//             actionToYesButton: actionFunction, 
//             textYesButton: yesButtonText, 
//             textNoButton: 'Cancelar' 
//         });
//         setShowConfirmModal(true);
//     };

//     const ReturnToDashboard = async () => {
//         try {
//             showMessage({ type: MESSAGE_TYPE.INFO, text: 'Regresando al Dashboard...' });
//             await BLResetCurrentDynamic();
//             getOutOfTheDynamic();
//         } catch (error) {

//         }
//     };

//     // ==========================================
//     // RENDERIZADO DEL PANEL
//     // ==========================================
//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             {/* Header y Estatus */}
//             <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
//                 <p className='AdmRunToGoal__Title mb-3 mb-md-0'>
//                     <i className='bi bi-phone-vibrate me-2'></i> Show de Luces
//                 </p>
//                 <div className="alert alert-dark py-2 px-3 mb-0 font-weight-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
//                     <i className="bi bi-broadcast-pin me-2"></i> Status: {currentSystemStatus}
//                 </div>
//             </div>

//             <div className="row">
//                 {/* BLOQUE 1: CONSTRUCTOR DE CICLOS */}
//                 <div className="col-md-7 mb-4">
//                     <div className="card shadow-sm h-100 border-dark">
//                         <div className="card-header bg-dark text-white font-weight-bold d-flex justify-content-between align-items-center">
//                             <span><i className="bi bi-palette-fill me-2"></i> 1. Ciclo de Colores (Frames)</span>
//                             <div className="d-flex align-items-center">
//                                 <label className="mb-0 me-2 small">BPM (Ritmo):</label>
//                                 <input 
//                                     type="number" 
//                                     className="form-control form-control-sm text-center font-weight-bold" 
//                                     style={{ width: '120px' }} 
//                                     value={beatsPerMinute} 
//                                     onChange={(e) => setBeatsPerMinute(parseInt(e.target.value) || 0)} 
//                                 />
//                             </div>
//                         </div>
//                         <div className="card-body p-0">
//                             <table className="table table-bordered table-hover mb-0 align-middle text-center">
//                                 <thead className="table-light">
//                                     <tr>
//                                         <th># de frame</th>
//                                         <th>Color de pantalla</th>
//                                         <th>¿Flash prendido o apagado?</th>
//                                         <th></th>
//                                     </tr>
//                                 </thead>
//                                 <tbody>
//                                     {animationFrames.map((frame, index) => (
//                                         <tr key={frame.id}>
//                                             <td className="font-weight-bold">{index + 1}</td>
//                                             <td>
//                                                 <div className="d-flex justify-content-center align-items-center">
//                                                     <input 
//                                                         type="color" 
//                                                         className="form-control form-control-color border-0 p-0 me-2" 
//                                                         value={frame.color} 
//                                                         onChange={(e) => updateFrame(frame.id, 'color', e.target.value)} 
//                                                         style={{ width: '40px', height: '30px' }} 
//                                                     />
//                                                     <span className="small text-muted font-monospace">{frame.color.toUpperCase()}</span>
//                                                 </div>
//                                             </td>
//                                             <td>
//                                                 <div className="form-check form-switch d-flex justify-content-center">
//                                                     <input 
//                                                         className="form-check-input" 
//                                                         type="checkbox" 
//                                                         checked={frame.flashOn} 
//                                                         onChange={(e) => updateFrame(frame.id, 'flashOn', e.target.checked)} 
//                                                         style={{ cursor: 'pointer', width: '40px', height: '20px' }} 
//                                                     />
//                                                 </div>
//                                             </td>
//                                             <td>
//                                                 <button className="btn btn-sm btn-outline-danger" onClick={() => removeFrame(frame.id)} title="Borrar Frame">
//                                                     <i className="bi bi-trash3-fill"></i>
//                                                 </button>
//                                             </td>
//                                         </tr>
//                                     ))}
//                                 </tbody>
//                             </table>
//                         </div>
//                         {/* FOOTER CON BOTONES DE MEMORIA Y PRESETS */}
//                         <div className="card-footer bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
//                             <button className="btn btn-sm btn-outline-success font-weight-bold" onClick={addFrame}>
//                                 <i className="bi bi-plus-circle-fill me-1"></i> Agregar Frame
//                             </button>
                            
//                             <div className="d-flex align-items-center flex-wrap gap-2">
//                                 <span className="text-muted small font-weight-bold">Presets:</span>
//                                 <button className={`btn btn-sm btn-${presetLoaded === PRESETS.BASICO ? 'success' : 'outline-secondary'}`} onClick={() => loadPreset(PRESETS.BASICO)}>Básico</button>
//                                 <button className={`btn btn-sm btn-${presetLoaded === PRESETS.NOPODRAS_KARAOKE ? 'success' : 'outline-secondary'}`} onClick={() => loadPreset(PRESETS.NOPODRAS_KARAOKE)}>No podrás (Karaoke)</button>
//                                 <button className={`btn btn-sm btn-${presetLoaded === PRESETS.KILOMETROS_KARAOKE ? 'success' : 'outline-secondary'}`} onClick={() => loadPreset(PRESETS.KILOMETROS_KARAOKE)}>Kilómetros (Karaoke)</button>
//                                 <button className={`btn btn-sm btn-${presetLoaded === PRESETS.SIRENA ? 'success' : 'outline-secondary'}`} onClick={() => loadPreset(PRESETS.SIRENA)}>Sirena</button>
                                
//                                 <span className="text-muted small font-weight-bold ms-2 border-start ps-3">Memoria:</span>
//                                 <button className="btn btn-sm btn-primary" onClick={saveCustomCycleToMemory} title="Guardar configuración actual en el navegador">
//                                     <i className="bi bi-floppy-fill me-1"></i> Guardar
//                                 </button>
//                                 <button className="btn btn-sm btn-outline-danger" onClick={clearCustomCycleFromMemory} title="Borrar lo guardado y regresar a básico">
//                                     <i className="bi bi-eraser-fill me-1"></i> Borrar
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 </div>

//                 {/* BLOQUE 2: BOTONERA DE CONTROLES MAESTROS */}
//                 <div className="col-md-5 mb-4">
//                     <div className="card shadow-sm h-100 border-primary">
//                         <div className="card-header bg-primary text-white font-weight-bold">
//                             <i className="bi bi-joystick me-2"></i> 2. Controles Maestros
//                         </div>
//                         <div className="card-body d-flex flex-column">
                            
//                             <div className="alert alert-primary bg-light border-primary pb-3 pt-3 mb-4 shadow-sm">
//                                 <h6 className="font-weight-bold text-primary text-center mb-3">PASOS A SEGUIR</h6>
//                                 <ol className="mb-0 small text-dark pl-3">
//                                     <li className="mb-2"><strong>Preparar:</strong> Los celulares mostrarán un botón para pedir permisos al usuario.</li>
//                                     <li className="mb-2"><strong>Iniciar:</strong> Los flashes comenzarán a parpadear al ritmo seleccionado.</li>
//                                     <li><strong>Detener:</strong> Apaga los flashes y libera las cámaras de los usuarios.</li>
//                                 </ol>
//                             </div>

//                             <button className="btn btn-outline-primary mb-3 btn-lg font-weight-bold" 
//                                 onClick={() => triggerModal('Esto enviará la configuración de colores a los dispositivos y pedirá permisos. ¿Continuar?', executePrepareShow, 'Paso 1: Preparar')}>
//                                 <i className="bi bi-person-raised-hand me-2"></i> Paso 1: PREPARAR / ENVIAR
//                             </button>
                            
//                             <button className="btn btn-success mb-4 btn-lg font-weight-bold shadow" 
//                                 onClick={() => triggerModal('¿Arrancar el show de luces en este momento o aplicar los nuevos BPM/Colores al show en curso?', executeStartShow, '¡ARRANCAR SHOW!')}>
//                                 <i className="bi bi-lightning-charge-fill me-2"></i> Paso 2: INICIAR / ACTUALIZAR
//                             </button>
                            
//                             <hr className="w-100 mt-auto" />
                            
//                             <button className="btn btn-danger font-weight-bold w-100" 
//                                 onClick={() => triggerModal('¿Detener el show y apagar las luces? (Los celulares seguirán en la pantalla listos por si quieres volver a iniciar).', executeStopShow, 'Sí, Detener')}>
//                                 <i className="bi bi-stop-circle-fill me-1"></i> Detener / Apagar Luces
//                             </button>

//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* INTEGRACIÓN DEL MODAL ESTANDARIZADO */}
//             {showConfirmModal && (
//                 <Confirm 
//                     message={confirmModalData?.message} 
//                     actionToYesButton={confirmModalData?.actionToYesButton} 
//                     textYesButton={confirmModalData?.textYesButton} 
//                     textNoButton={confirmModalData?.textNoButton} 
//                     setShowModal={setShowConfirmModal} 
//                     setConfirmData={setConfirmModalData} 
//                 />
//             )}
//         </SecondaryBase>
//     )
// }

// export { LightShow };